/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import PageTitle from "../../layouts/PageTitle";
import { fetchConnectedUser } from "../../../services/OrdersService";
import {
  getDashboardArchives,
} from "../../../utils/dashboardArchiveStorage";
import { downloadDashboardSnapshotPdf } from "../../../utils/dashboardArchivePdf";
import { useTranslation } from "react-i18next";
import { formatMoney } from "../../../utils/currency";

export default function DashboardArchives() {
  const { t } = useTranslation();
  const [scopeId, setScopeId] = useState(null);
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const user = await fetchConnectedUser();
        if (cancelled) return;
        const id = String(user?.restaurantFK || user?._id || "");
        setScopeId(id || null);
        setArchives(getDashboardArchives(id));
      } catch {
        if (!cancelled) {
          setScopeId(null);
          setArchives([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePdf = (snap) => {
    const name = `dashboard-archive-${snap.id || snap.archivedAt || "export"}.pdf`;
    const withFormat = {
      ...snap,
      totalRevenueFormatted:
        snap.totalRevenueFormatted ?? formatMoney(Number(snap.totalRevenue || 0)),
    };
    downloadDashboardSnapshotPdf(withFormat, name);
  };

  if (loading) {
    return (
      <div className="p-4">
        <PageTitle activeMenu={t("dashboardArchives")} motherMenu={t("Archived")} />
        <p className="text-muted">…</p>
      </div>
    );
  }

  return (
    <div className="dashboard-archives-page">
      <div className="row mb-3">
        <div className="col-12">
          <PageTitle activeMenu={t("dashboardArchives")} motherMenu={t("Archived")} />
          <p className="text-muted fs-14 mb-0">{t("dashboardArchivesIntro")}</p>
        </div>
      </div>

      {!scopeId ? (
        <div className="alert alert-warning">{t("dashboardArchivesNoScope")}</div>
      ) : archives.length === 0 ? (
        <div className="card">
          <div className="card-body text-muted">{t("dashboardArchivesEmpty")}</div>
        </div>
      ) : (
        <div className="row g-3">
          {archives.map((snap) => (
            <div key={snap.id} className="col-12 col-lg-6">
              <div className="card h-100 border shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <div>
                      <h5 className="mb-1 fs-16">{t("dashboardArchiveCardTitle")}</h5>
                      <div className="text-muted fs-12">
                        {snap.archivedAtLabel || snap.archivedAt}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => handlePdf(snap)}
                    >
                      PDF
                    </button>
                  </div>
                  <ul className="list-unstyled fs-13 mb-0 text-dark">
                    <li>
                      <strong>{t("totalRevenue")}:</strong>{" "}
                      {snap.totalRevenueFormatted ??
                        formatMoney(Number(snap.totalRevenue || 0))}
                    </li>
                    <li>
                      <strong>{t("totalOrders")}:</strong> {snap.totalOrders ?? 0}
                    </li>
                    <li>
                      <strong>{t("bestSellingProduct")}:</strong>{" "}
                      {snap.bestSellingLabel || "—"}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
